'use client'

import React from 'react'
import { Modal } from 'design-configuration/components'
import { XIcon } from 'design-configuration/components/icons'
import { PaginationDirection } from '@components/pagination-button'
import { useSoloViewer } from '../provider'
import { SoloImageDisplay } from './solo-image-display'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { cn } from '@/lib/utils'

export const ImageSoloViewer = (): React.ReactElement => {
  const { soloViewerData, setSoloViewerImageData } = useSoloViewer()
  const { image, imageList } = soloViewerData

  const onDismiss = () => {
    setSoloViewerImageData({ imageList: undefined, image: undefined })
  }

  const findImageIndex = (img?: string): number | undefined => {
    if (!img) {
      return undefined
    }
    return imageList?.findIndex((t) => t === img)
  }

  const [imageIndex, setImageIndex] = React.useState<number | undefined>(undefined)

  // We need these refs for the keyboard event listener to work
  const imageIndexRef = React.useRef<number | undefined>(imageIndex)
  const imageListRef = React.useRef<string[] | undefined>(imageList)

  React.useEffect(() => {
    setImageIndex(findImageIndex(image))
  }, [JSON.stringify(image), image])

  React.useEffect(() => {
    imageIndexRef.current = imageIndex
  }, [imageIndex])
  React.useEffect(() => {
    imageListRef.current = imageList
  }, [imageList])

  const updateImageIndex = (direction: PaginationDirection) => {
    if (!imageListRef.current) {
      return
    }
    const currentImageIndex = imageIndexRef.current ?? 0
    let newIndex = (currentImageIndex ?? 0) + (direction === PaginationDirection.Right ? 1 : -1)

    const images = imageListRef.current
    if (newIndex < 0) {
      newIndex = images.length - 1
    } else if (newIndex > images.length - 1) {
      newIndex = 0
    }
    const newImage = images[newIndex]

    setSoloViewerImageData({ imageList: images, image: newImage })
  }

  const selectedImage = imageList && imageIndex !== undefined && imageIndex !== -1 ? imageList[imageIndex] : image

  const handleKeyPress = (event: any) => {
    switch (event.key) {
      case 'ArrowLeft':
        updateImageIndex(PaginationDirection.Left)
        break
      case 'ArrowRight':
        updateImageIndex(PaginationDirection.Right)
        break
      case 'Escape':
        onDismiss()
        break
      default:
        break
    }
  }

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  return (
    <Modal
      modalType="full"
      show={image !== undefined}
      dismissOnClickOutside
      onDismiss={() => {
        onDismiss()
      }}
    >
      <button
        className="p-xs absolute right-0 top-0 z-[80] flex items-center justify-center"
        onClick={() => onDismiss()}
      >
        <XIcon className="fill-on-surface-variant w-sm h-sm" />
      </button>
      <div
        className="px-md gap-y-md py-md relative flex h-[100dvh] w-full flex-col items-center justify-center"
        onClick={() => {
          onDismiss()
        }}
      >
        {selectedImage ? (
          <SoloImageDisplay
            image={selectedImage}
            numImages={imageList?.length ?? 0}
            onChangeImage={(direction: PaginationDirection) => updateImageIndex(direction)}
          />
        ) : null}
        <TinyImageGallery
          imageList={imageList ?? []}
          currentImageIndex={imageIndex ?? 0}
          onImageClick={setImageIndex}
        />
      </div>
    </Modal>
  )
}

type TinyImageGalleryProps = {
  imageList: string[]
  currentImageIndex: number
  onImageClick: (newIndex: number) => void
  className?: string
}

export function TinyImageGallery({
  imageList,
  currentImageIndex,
  onImageClick,
  className
}: TinyImageGalleryProps): React.ReactElement {
  if (imageList.length === 0) {
    return <></>
  }
  return (
    <div key="tiny-gallery" className={cn(`gap-x-2xs relative flex h-fit`, className)}>
      {imageList.map((image, index) => (
        <div
          key={image}
          className={cn(
            `h-lg w-lg relative aspect-square cursor-pointer opacity-60 transition-opacity duration-100 hover:opacity-100`,
            {
              'outline-primary-8 opacity-100 shadow-md outline outline-1': currentImageIndex === index
            }
          )}
          onClick={(event) => {
            event.stopPropagation()
            onImageClick(index)
          }}
        >
          <ImageWithCustomLoader
            src={image}
            alt={image}
            priority
            fill
            style={{
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            sizes="5vw"
          />
        </div>
      ))}
    </div>
  )
}
